import { useAtom } from "jotai";
import {
  Checkbox,
  Color,
  Flex,
  HorizontalDivider,
  Select,
  SmallNumberInput,
  Text,
} from "src/elements";

import { planogramSelectedItemsAtom } from "src/components/PlanogramExtensions/store/atoms";
import { usePogActions } from "src/components/Planogram/store/actions";
import { useTranslation } from "src/i18n";
import { CheckboxProps } from "src/elements/Checkbox/Checkbox";
import { ItemCdtEditor } from "../components/ItemCdtEditor";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { isPlanogramItemSelection } from "src/components/Planogram/common/planogramSelection";
import {
  PLANOGRAM_ORIENTATION_MIXED,
  getPlanogramOrientationSelectState,
} from "../components/planogramOrientation";

interface Props {
  pog: Pog;
  setPog: React.Dispatch<React.SetStateAction<Pog>>;
}

export const ProductsDetails = ({ pog, setPog }: Props) => {
  const { t } = useTranslation("common");
  const {
    setHorizontalFacings,
    setOrientation,
    setSqueezeDimension,
    setVerticalFacings,
    saveChanges,
  } = usePogActions({
    pog,
    setPog,
  });

  const [selectedItems] = useAtom(planogramSelectedItemsAtom);
  const selectedShelfItems = selectedItems.filter(isPlanogramItemSelection);
  const cdtSourceItems = pog.planogram.getAllPlanogramProducts();
  const orientationState = getPlanogramOrientationSelectState(selectedShelfItems);

  if (selectedShelfItems.length === 0) {
    return null;
  }

  const getHorizontalFacings = () => {
    let largestHorizontalFacings = 0;

    for (const selectedItem of selectedShelfItems) {
      const itemHorizontalFacings = selectedItem.facings / selectedItem.facingsRows;
      if (itemHorizontalFacings > largestHorizontalFacings) {
        largestHorizontalFacings = itemHorizontalFacings;
      }
    }

    return largestHorizontalFacings;
  };

  const getVerticalFacings = () => {
    let largestVerticalFacings = 0;

    for (const selectedItem of selectedShelfItems) {
      const itemVerticalFacings = selectedItem.facingsRows;
      if (itemVerticalFacings > largestVerticalFacings) {
        largestVerticalFacings = itemVerticalFacings;
      }
    }

    return largestVerticalFacings;
  };

  const getSqueezeDimension = (
    dimension: "squeezeWidth" | "squeezeHeight" | "squeezeDepth",
  ): CheckboxProps["isChecked"] => {
    const squeezeCheck = selectedShelfItems[0][dimension];

    for (const selectedItem of selectedShelfItems) {
      if (selectedItem[dimension] != squeezeCheck) {
        return "mixed";
      }
    }

    return squeezeCheck;
  };

  return (
    <Flex width="100%" column padding="10px" gap="10px">
      <Text variant="small1" color={Color.primary}>
        {t("planogram.facingsAndPlacement")}
      </Text>

      <Flex gap="10px" justify="between">
        <SmallNumberInput
          label="Horizontal Facings:"
          variant="sidebuttons"
          textVariant="caption1"
          textColor={Color.primaryActive}
          width="20px"
          value={getHorizontalFacings()}
          setValue={(horizontalFacings) =>
            setHorizontalFacings(selectedShelfItems, horizontalFacings)
          }
          min={1}
        />

        <SmallNumberInput
          label="Vertical Facings:"
          variant="sidebuttons"
          textVariant="caption1"
          textColor={Color.primaryActive}
          width="20px"
          value={getVerticalFacings()}
          setValue={(verticalFacings) => setVerticalFacings(selectedShelfItems, verticalFacings)}
          min={1}
        />
      </Flex>

      <Flex gap="10px" justify="space-between">
        <Text variant="caption1" color={Color.primaryActive}>
          Squeeze:
        </Text>

        <Flex gap="8px">
          <Checkbox
            isCheckboxLeft
            label="Width"
            isChecked={getSqueezeDimension("squeezeWidth")}
            size="small"
            setIsChecked={(isChecked) =>
              setSqueezeDimension(selectedShelfItems, "squeezeWidth", isChecked)
            }
          />

          <Checkbox
            isCheckboxLeft
            label="Height"
            isChecked={getSqueezeDimension("squeezeHeight")}
            size="small"
            setIsChecked={(isChecked) =>
              setSqueezeDimension(selectedShelfItems, "squeezeHeight", isChecked)
            }
          />

          <Checkbox
            isCheckboxLeft
            label="Depth"
            isChecked={getSqueezeDimension("squeezeDepth")}
            size="small"
            setIsChecked={(isChecked) =>
              setSqueezeDimension(selectedShelfItems, "squeezeDepth", isChecked)
            }
          />
        </Flex>
      </Flex>

      <Flex gap="10px" justify="between" align="center">
        <Text variant="caption1" color={Color.primaryActive}>
          Orientation:
        </Text>

        <Select
          width="min-width"
          size="small"
          options={orientationState.options}
          value={orientationState.value}
          isDisabled={orientationState.isDisabled}
          setValue={(orientation) => {
            if (orientation !== PLANOGRAM_ORIENTATION_MIXED) {
              setOrientation(selectedShelfItems, orientation);
            }
          }}
        />
      </Flex>

      <HorizontalDivider />

      <ItemCdtEditor
        selectedItems={selectedShelfItems}
        cdtSourceItems={cdtSourceItems}
        saveChanges={saveChanges}
      />
    </Flex>
  );
};
